import React__default, { useState, useCallback } from 'react';
import { TrapFocus, Portal } from '@shopify/polaris';
import { useShortcut } from '@shopify/react-shortcuts';
import classNames from 'classnames';
import { IS_PROD_ENV } from '../../utilities/env.mjs';
import styles from './Popover.scss.mjs';
import { popoverLayoutToCss } from './strategies/utilities/layout.mjs';
import { useMeasurements } from './hooks/useMeasurements.mjs';
import { OutsideClickDetection } from './components/OutsideClickDetection/OutsideClickDetection.mjs';
import { Card } from './components/Card/Card.mjs';
import { customStrategy } from './strategies/customStrategy/customStrategy.mjs';
import { sidebarStrategy } from './strategies/sidebarStrategy/sidebarStrategy.mjs';
import { preferCenteredBelowStrategy } from './strategies/preferCenteredBelowStrategy/preferCenteredBelowStrategy.mjs';
import { Backdrop } from './components/Backdrop/Backdrop.mjs';

let CloseSource = /*#__PURE__*/function (CloseSource) {
  CloseSource[CloseSource["ClickOutside"] = 0] = "ClickOutside";
  CloseSource[CloseSource["EscapeKeypress"] = 1] = "EscapeKeypress";
  return CloseSource;
}({});
function Popover({
  id,
  children,
  role,
  preventPortal,
  onClose,
  anchor,
  visibility,
  zIndexOverride,
  backdrop,
  ...strategyAndOptions
}) {
  const defaultBackdropProps = {
    transparent: true,
    zIndexOverride: zIndexOverride === undefined ? undefined : zIndexOverride - 1
  };
  const activator = getPopoverActivator(id);
  const [popover, setPopoverRef] = useState(null);
  if (!IS_PROD_ENV && !activator && !anchor) {
    console.warn('Popover requires either an activator or an anchor element. If the popover should be positioned relative to the activator, spread `activatorAccessibilityProps` to the activator. If it should be positioned relative to the anchor, pass it using the anchor prop.');
  }
  const measurements = useMeasurements({
    anchor: anchor !== null && anchor !== void 0 ? anchor : activator,
    popover
  });
  const layout = getLayout(strategyAndOptions, measurements);
  const style = layout ? popoverLayoutToCss({
    ...layout,
    zIndex: zIndexOverride ? zIndexOverride : layout.zIndex
  }) : undefined;
  const handleEscape = useCallback(() => {
    if (visibility === 'hidden') {
      return;
    }
    onClose === null || onClose === void 0 ? void 0 : onClose({
      source: CloseSource.EscapeKeypress
    });
    activator === null || activator === void 0 ? void 0 : activator.focus();
  }, [onClose, activator, visibility]);
  const handleOutsideClick = useCallback(event => {
    if (visibility === 'hidden') {
      return;
    }
    const {
      target
    } = event;

    // Prevent click event on the activator from being called after `onClose` callback
    if (event.target instanceof Element && event.target.closest(`[aria-controls="${id}"]`)) {
      event.stopPropagation();
      event.preventDefault();
    }
    onClose === null || onClose === void 0 ? void 0 : onClose({
      source: CloseSource.ClickOutside,
      target
    });
    activator === null || activator === void 0 ? void 0 : activator.focus();
  }, [id, onClose, activator, visibility]);

  // `ignoreInput` allows the key to be matched when typed from within an input field
  useShortcut(['Escape'], handleEscape, {
    ignoreInput: true
  });
  function renderBackdrop() {
    if (!backdrop || visibility === 'hidden') {
      return null;
    }
    if (backdrop === true) {
      return /*#__PURE__*/React__default.createElement(Backdrop, defaultBackdropProps);
    }
    return /*#__PURE__*/React__default.createElement(Backdrop, Object.assign({}, defaultBackdropProps, backdrop));
  }

  // TODO: `TrapFocus` doesn't work when first `ActionList` item is `active`.
  const markup = /*#__PURE__*/React__default.createElement(React__default.Fragment, null, renderBackdrop(), /*#__PURE__*/React__default.createElement(TrapFocus, {
    trapping: visibility !== 'hidden' && role === 'dialog'
  }, /*#__PURE__*/React__default.createElement(OutsideClickDetection, {
    callback: handleOutsideClick
  }, /*#__PURE__*/React__default.createElement("div", {
    id: id,
    style: style,
    className: classNames(styles.Popover, visibility === 'hidden' && styles.hidden),
    role: role,
    ref: setPopoverRef
  }, children))));
  return preventPortal ? markup : /*#__PURE__*/React__default.createElement(Portal, null, markup);
}
Popover.Card = Card;
function getLayout(strategy, measurements) {
  const {
    strategy: strategyName,
    options
  } = strategy;
  switch (strategyName) {
    case 'prefer-centered-below':
      return preferCenteredBelowStrategy({
        options,
        measurements
      });
    case 'sidebar':
      return sidebarStrategy({
        options,
        measurements
      });
    case 'custom':
      return customStrategy({
        options,
        measurements
      });
  }
}
function getPopoverActivator(id) {
  const activator = document.querySelector(`[aria-controls="${id}"]`);
  return activator;
}

export { CloseSource, Popover };
